import dynamic from "next/dynamic";
import classNames from "classnames";
import Balancer from "react-wrap-balancer";
import Button from "components/buttonNew/button";
import { SelfServePageData } from "../fetcher";
import { Fragment } from "react";
import type { SubscriptionLevel } from "lib/types";

const Cta = dynamic(() => import("./cta"), {
  ssr: false,
});

type ComparisonProps = {
  data: SelfServePageData;
  readerSubscriptionLevel: SubscriptionLevel;
  onButtonClick: () => void;
};

const TickIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mx-auto"
  >
    <path
      d="M7 11.7895L10.8462 16L17 8"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const CrossIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mx-auto"
  >
    <path d="M7 7L17 17" stroke="currentColor" strokeWidth="2" />
    <path d="M7 17L17 7" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const Table = ({ data }) => {
  return (
    <div className="max-w-full overflow-auto bg-white text-left lg:max-w-[660px]">
      <div className="inline-block w-full">
        <div className="grid grid-cols-[minmax(200px,1fr),90px,90px] border-[#E3E3E5] lg:grid-cols-[auto,90px,90px]">
          <div className="sticky left-0 border-[1px] bg-white p-5 font-recklessBold text-[18px] max-md:px-3">
            {data.comparisonTableFeatureColumnName}
          </div>
          <div className="border-b-[1px] border-t-[1px] p-5 text-center font-recklessBold text-[18px] max-md:px-3">
            Angel
          </div>
          <div className="border-[1px] p-5 text-center font-recklessBold text-[18px] max-md:px-3">
            Pro
          </div>
          {data.comparisonTable?.length > 0 &&
            data.comparisonTable.map((item, i) => (
              <Fragment key={i}>
                <div className="sticky left-0 border-b-[1px] border-l-[1px] border-r-[1px] bg-white p-5 text-[16px] leading-[130%] max-md:px-3">
                  {item.feature}
                </div>
                <div className="flex items-center justify-center border-b-[1px] p-5 text-center text-[16px] max-md:px-3">
                  {item.sfaValueOverride ? (
                    item.sfaValueOverride
                  ) : item.doesSfaIncludeThis ? (
                    <TickIcon />
                  ) : (
                    <CrossIcon />
                  )}
                </div>
                <div className="flex items-center justify-center border-b-[1px] border-l-[1px] border-r-[1px] p-5 text-center text-[16px] max-md:px-3">
                  {item.proValueOverride ? (
                    item.proValueOverride
                  ) : item.doesProIncludeThis ? (
                    <TickIcon />
                  ) : (
                    <CrossIcon />
                  )}
                </div>
              </Fragment>
            ))}
        </div>
      </div>
    </div>
  );
};

const Heading = ({ className = "", text }) => (
  <p
    className={classNames(
      className,
      "font-recklessBold text-[32px] leading-[130%]"
    )}
  >
    <Balancer>{text}</Balancer>
  </p>
);

export const Comparison = ({
  data,
  readerSubscriptionLevel,
  onButtonClick,
}: ComparisonProps) => {
  return (
    <div className="mt-10 md:mt-[70px]">
      <Heading
        className={"mb-[30px] lg:hidden"}
        text={data.comparisonHeading}
      />

      <div className="flex flex-col gap-x-5 lg:flex-row-reverse lg:justify-between">
        <Table data={data} />

        <div className="flex flex-col gap-y-5">
          <Heading
            className={"hidden lg:block"}
            text={data.comparisonHeading}
          />
          <Cta
            data={data}
            className={"mt-5 md:mt-10"}
            readerSubscriptionLevel={readerSubscriptionLevel}
            onButtonClick={onButtonClick}
            textOverride={data.comparisonPrimaryCtaText}
          />
          <Button
            size={"medium"}
            colour={"navy"}
            buttonType="secondary"
            className={classNames("max-w-fit disabled:bg-transparent", {})}
            component="link"
            href={data.comparisonSecondaryCtaUrl}
          >
            {data.comparisonSecondaryCtaText}
          </Button>
        </div>
      </div>
    </div>
  );
};
